import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

// Define the form schema with Zod
const sensorFormSchema = z.object({
  sensor_name: z.string().min(1, {
    message: "Sensor name is required.",
  }),
  latitude: z.number().min(-90).max(90, {
    message: "Latitude must be between -90 and 90.",
  }),
  longitude: z.number().min(-180).max(180, {
    message: "Longitude must be between -180 and 180.",
  }),
});

// Infer the form values type from the schema
type SensorFormValues = z.infer<typeof sensorFormSchema>;

interface SensorFormProps {
  onSubmit: (data: { name: string; latitude: number; longitude: number }) => void;
  initialValues?: Partial<SensorFormValues>;
  isSubmitting?: boolean;
}

export function SensorForm({
  onSubmit,
  initialValues,
  isSubmitting = false,
}: SensorFormProps) {
  // Initialize the form with react-hook-form
  const form = useForm<SensorFormValues>({
    resolver: zodResolver(sensorFormSchema),
    defaultValues: {
      sensor_name: initialValues?.sensor_name || "",
      latitude: initialValues?.latitude || 0,
      longitude: initialValues?.longitude || 0,
    },
  });

  // Handle form submission
  const handleSubmit = (values: SensorFormValues) => {
    onSubmit({
      name: values.sensor_name,
      latitude: values.latitude,
      longitude: values.longitude,
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="sensor_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sensor Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter sensor name" {...field} />
              </FormControl>
              <FormDescription>
                A descriptive name for this sensor.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="latitude"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Latitude</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="any"
                    placeholder="0.0"
                    {...field}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                </FormControl>
                <FormDescription>
                  Latitude coordinate (-90 to 90).
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="longitude"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Longitude</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="any"
                    placeholder="0.0"
                    {...field}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                </FormControl>
                <FormDescription>
                  Longitude coordinate (-180 to 180).
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </form>
    </Form>
  );
} 